import { Box, Button, Divider, Slider, Stack, styled, Typography, useTheme } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';
import { Info, ShoppingBag } from 'react-feather';
import { useNavigate } from 'react-router-dom';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import ImageComponent from 'src/components/common/Image';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import PopupCancelPlan from 'src/features/Setting/PlanUsage/PopupCancelPlan';

// Utilities
import { useQueryUsageInformation } from 'src/services/queries';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { ICON_NO_PLAN_AND_USAGE } from 'src/utils/constants/imagesUrl';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { formatUTCTime, getPeriodOfMonths } from 'src/utils/helpers/dayjs';
import { getPlanTypeFromTime, renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { checkExistSubscription } from 'src/utils/helpers/subscription';

const StyledSlider = styled(Slider)(({ theme }) => ({
  width: '100%',
  '&.Mui-disabled': {
    color: theme.palette.secondary.main,
  },
  height: 10,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    backgroundColor: 'transparent',
    border: 'none',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: theme.palette.background.paper1,
  },
}));

const Usage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data, refetch, isFetching } = useQueryUsageInformation();
  const [openCancel, setOpenCancel] = useState(false);

  const durationOfPlan = getPeriodOfMonths(data?.data?.created_at, data?.data?.period_end);

  const planType = getPlanTypeFromTime(durationOfPlan);

  if (isFetching) {
    return <LoadingSkeleton count={5} height={30} />;
  }

  return (
    <React.Fragment>
      <PopupCancelPlan
        open={openCancel}
        handleClose={() => setOpenCancel(false)}
        refetchUsage={refetch}
        subscriptionId={data?.data?.subscription_id}
      />

      {checkExistSubscription(data) ? (
        <Stack direction="column" spacing={2}>
          <Box>
            <Typography variant="h6">{t('setting.plan_usage_manage.subscription.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('setting.plan_usage_manage.subscription.sub_title')
                .replace('{{plan_name}}', renderTextOrDefault(data?.data?.current_product || '').toString())
                .replace(
                  '{{plan_type}}',
                  data?.data?.is_trial ? t('pricing.plan_type.free_trial') : t(`pricing.plan_type.${planType}`),
                )}
            </Typography>
          </Box>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.subscription.type')}
              </Typography>
              <Typography variant="subtitle1">
                {data?.data?.is_trial ? t('pricing.plan_type.free_trial') : t(`pricing.plan_type.${planType}`)}
              </Typography>
            </Box>
            {/* <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('setting.plan_usage_manage.subscription.member_limit')}
                </Typography>
                <Typography variant="subtitle1">5</Typography>
              </Box> */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.subscription.registration_date')}
              </Typography>
              <Typography variant="subtitle1">
                {formatUTCTime(FORMAT_DATE_TIME.MMM_DD_YYYY, data?.data?.created_at)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.subscription.renewal_date')}
              </Typography>
              <Typography variant="subtitle1">
                {data.data.is_trial ? formatUTCTime(FORMAT_DATE_TIME.MMM_DD_YYYY, data?.data?.period_end) : '--'}
              </Typography>
            </Box>
          </Stack>
          <Divider />
          <Box>
            <Typography variant="subtitle1"> {t('setting.plan_usage_manage.subscription.plan_usage')}</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.subscription.credit_points')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {renderNumberOrDefault(data?.data?.current_usage)}/{renderNumberOrDefault(data?.data?.usage_total)}
              </Typography>
            </Stack>
            <StyledSlider value={data?.data?.current_usage} min={0} max={data?.data?.usage_total} disabled />
          </Box>
          <Stack
            direction="row"
            p={2}
            border={`1px solid ${theme.palette.info.main}`}
            borderRadius={theme.shape.borderRadius / 4}
            spacing={1.5}
            alignItems="flex-start"
          >
            <IconFeather
              icon={<Info />}
              muiProps={{
                color: 'info',
                fontSize: 'small',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('setting.plan_usage_manage.subscription.subscription_will_auto_renew_on')}{' '}
              {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.data?.period_end)}
            </Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="subtitle1" color="text.secondary">
              {t('setting.plan_usage_manage.subscription.total')}
            </Typography>
            <Stack spacing={1}>
              <Typography variant="subtitle1" align="right">
                USD {renderNumberOrDefault(data?.data?.invoice_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="right">
                {t('setting.plan_usage_manage.subscription.exclude_vat')}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="right">
                {t('setting.plan_usage_manage.subscription.next_payment')}{' '}
                {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.data?.period_end)}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <ButtonWithLoading variant="outlined" color="error" size="medium" onClick={() => setOpenCancel(true)}>
              {t('setting.plan_usage_manage.subscription.cancel_plan')}
            </ButtonWithLoading>
            {/* <ButtonWithLoading
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={() => navigate(AuthRouteKeys.Pricing)}
            >
              {t('setting.plan_usage_manage.subscription.upgrade_plan')}
            </ButtonWithLoading> */}
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="column"
          spacing={5}
          justifyContent="center"
          alignItems="center"
          bgcolor={theme.palette.background.paper1}
          borderRadius={theme.shape.borderRadius / 2}
          py={10}
        >
          <ImageComponent imgSrc={ICON_NO_PLAN_AND_USAGE} height={150} />
          <Box width="60%">
            <Typography variant="subtitle1" align="center">
              {t('setting.plan_usage_manage.billing_detail.you_do_not_have_credits')}
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              {t('setting.plan_usage_manage.billing_detail.pls_subscription_plan')}
            </Typography>
          </Box>
          <Box>
            <Button
              color="primary"
              variant="contained"
              size="large"
              endIcon={<IconFeather icon={<ShoppingBag />} />}
              onClick={() => navigate(AuthRouteKeys.Pricing)}
            >
              {t('setting.plan_usage_manage.billing_detail.pls_choose_plan')}
            </Button>
          </Box>
        </Stack>
      )}
    </React.Fragment>
  );
};

export default Usage;
